import React, { useState, useEffect } from "react";
import "./VTHome.css";
import VTInsulationUpdate from "./VTInsulationUpdate";
import VTCheckingUpdate from "./VTCheckingUpdate";
import VTRatioUpdate from "./VTRatioUpdate";
import VTListUpdate from "./VTListUpdate";
import CreateReport from "./CreateReport";
import { API_URL } from "./API";

const VTHomeUpdate = ({ project, goBack, reportToUpdate, isUpdateMode }) => {
  const windings = ["1a - 1n", "2a - 2n", "3a - 3n", "4a - 4n", "da - dn"];
  const kneePointTypes = [
    "0.1", "0.2", "0.5", "0.2S", "0.5S", "0.1S", "0.3",
    "5P10", "5P20", "10P10", "P5", "PX", "PR", "TPX", "TPY", "TPZ"
  ];

  const [selectedWinding, setSelectedWinding] = useState("");
  const [windingDetails, setWindingDetails] = useState([]);
  const [currentPage, setCurrentPage] = useState("home");
  const [showBackPage, setShowBackPage] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const initialFormData = {
    general: {
      description: "VT INSPECTION / TEST REPORT", date: "", customer: "", project: "", reportNo: "",
      location: "", client: "", yom: "", il: "", vf: "", frequency: "",
      type: "", specNo: "", insulationClass: "", make: "", serialNo: "",
      report_id: "",
      report_file: {} 
    },
    winding: "",
    windingDetails: [],
    insulationData: [],
    windingResistance: [],
    polarityCheck: [],
    ratioTestData: {},
    checklistData: []
  };

  const [allFormData, setAllFormData] = useState(initialFormData);

  const fetchClientName = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/clients/get_client`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
      });

      if (response.ok) {
        const result = await response.json();
        let clientsData = Array.isArray(result) ? result : result.data || result.clients || [];
        const client = clientsData.find(c => c.client_id === clientId || c.id === clientId);
        return client ? client.client_name : "Unknown Client";
      }
    } catch (error) {
      console.error("Error fetching client details:", error);
    }
    return "Unknown Client";
  };

  const getWindingCount = (winding) => {
    let windingStr = winding;
    if (winding && typeof winding === 'object' && winding.value) {
      windingStr = winding.value;
    }
    if (typeof windingStr !== 'string') {
      windingStr = String(windingStr || '');
    }
    if (!windingStr || windingStr.trim() === '') {
      return 0;
    }
    if (windingStr.toLowerCase().startsWith("da")) {
      return 5;
    } else {
      const match = windingStr.match(/^(\d)/);
      return match ? parseInt(match[1]) : 0;
    }
  };

  useEffect(() => {
    if (isUpdateMode && reportToUpdate && reportToUpdate.test_data) {
      try {
        console.log('=== Loading VT Report for Update ===');
        const td = reportToUpdate.test_data || {};

        const generalFromReport = td.general || {};
        const reportId = reportToUpdate.report_id || reportToUpdate.id || reportToUpdate._id;

        // Prevent re-hydration if we already have this report loaded
        if (allFormData.general.report_id === reportId) {
          console.log('Skipping re-hydration, report already loaded');
          return;
        }

        generalFromReport.report_id = reportId;
        // Capture report_file info
        generalFromReport.report_file = reportToUpdate.report_file || {};

        // Capture project hierarchy for folder structure preservation
        generalFromReport.sub_project_name = reportToUpdate.sub_project_name || td.sub_project_name || "";
        generalFromReport.category_name = reportToUpdate.category_name || td.category_name || "";
        generalFromReport.subcategory_name = reportToUpdate.subcategory_name || td.subcategory_name || "";
        generalFromReport.equipment_name = reportToUpdate.equipment_name || td.equipment_name || "";

        let windingFromReport = '';
        if (td.winding) {
          if (typeof td.winding === 'object' && td.winding.value) {
            windingFromReport = td.winding.value;
          } else if (typeof td.winding === 'string') {
            windingFromReport = td.winding;
          } else {
            windingFromReport = String(td.winding);
          }
        }

        let windingDetailsFromReport = [];
        if (td.windingDetails) {
          if (Array.isArray(td.windingDetails)) {
            windingDetailsFromReport = td.windingDetails;
          } else if (typeof td.windingDetails === 'object') {
            windingDetailsFromReport = Object.values(td.windingDetails);
          }
        }

        let insulationDataFromReport = [];
        if (td.insulationData) {
          if (Array.isArray(td.insulationData)) {
            insulationDataFromReport = td.insulationData;
          } else if (typeof td.insulationData === 'object') {
            insulationDataFromReport = Object.values(td.insulationData);
          }
        }

        let windingResistanceFromReport = [];
        if (td.windingResistance) {
          if (Array.isArray(td.windingResistance)) {
            windingResistanceFromReport = td.windingResistance;
          } else if (typeof td.windingResistance === 'object') {
            windingResistanceFromReport = Object.values(td.windingResistance);
          }
        }

        let polarityCheckFromReport = [];
        if (td.polarityCheck) {
          if (Array.isArray(td.polarityCheck)) {
            polarityCheckFromReport = td.polarityCheck;
          } else if (typeof td.polarityCheck === 'object') {
            polarityCheckFromReport = Object.values(td.polarityCheck);
          }
        }

        const ratioTestDataFromReport = td.ratioTestData || {};

        let checklistDataFromReport = [];
        if (td.checklistData) {
          if (Array.isArray(td.checklistData)) {
            checklistDataFromReport = td.checklistData;
          } else if (typeof td.checklistData === 'object') {
            checklistDataFromReport = Object.values(td.checklistData);
          }
        }

        setAllFormData(prev => ({
          ...prev,
          general: {
            ...prev.general,
            ...generalFromReport,
          },
          winding: windingFromReport,
          windingDetails: windingDetailsFromReport,
          insulationData: insulationDataFromReport,
          windingResistance: windingResistanceFromReport,
          polarityCheck: polarityCheckFromReport,
          ratioTestData: ratioTestDataFromReport,
          checklistData: checklistDataFromReport,
        }));

        setSelectedWinding(windingFromReport || "");
        const expectedCount = getWindingCount(windingFromReport || "");

        const normalizedDetails = Array.from({ length: expectedCount }, (_, index) => {
          return windingDetailsFromReport && windingDetailsFromReport[index]
            ? windingDetailsFromReport[index]
            : { ratio: "", vaPh: "", class: "", volts: "" };
        });
        setWindingDetails(normalizedDetails);

        localStorage.setItem('vtFormDataUpdate', JSON.stringify({
          ...generalFromReport,
          winding: windingFromReport,
          windingDetails: normalizedDetails,
          insulationData: insulationDataFromReport,
          windingResistance: windingResistanceFromReport,
          polarityCheck: polarityCheckFromReport,
          ratioTestData: ratioTestDataFromReport,
          checklistData: checklistDataFromReport
        }));

      } catch (e) {
        console.error('Failed to hydrate VT update form from report:', e);
      }
    } else {
      const savedData = localStorage.getItem('vtFormDataUpdate');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (parsedData) {
            setAllFormData(parsedData);
            if (parsedData.winding) {
              setSelectedWinding(parsedData.winding);
              setWindingDetails(parsedData.windingDetails || []);
            }
          }
        } catch (e) { console.error(e); }
      } else if (project) {
        const loadProjectData = async () => {
          let clientName = project?.clientName || project?.client_name;
          if (!clientName && project?.client) {
            clientName = await fetchClientName(project.client);
          }
          setAllFormData(prev => ({
            ...prev,
            general: {
              ...prev.general,
              project: project?.name || '',
              client: clientName || ''
            }
          }));
        };
        loadProjectData();
      }
    }
  }, [isUpdateMode, reportToUpdate, project]);

  useEffect(() => {
    localStorage.setItem('vtFormDataUpdate', JSON.stringify(allFormData));
  }, [allFormData]);

  useEffect(() => {
    if (selectedWinding && selectedWinding !== allFormData.winding) {
      const count = getWindingCount(selectedWinding);
      const newDetails = Array.from({ length: count }, (_, index) => {
        return windingDetails[index] || { ratio: "", vaPh: "", class: "", volts: "" };
      });
      setWindingDetails(newDetails);
      setAllFormData(prev => ({
        ...prev,
        winding: selectedWinding,
        windingDetails: newDetails
      }));
    }
  }, [selectedWinding]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    let newVal = value;
    let error = "";
    if (["description", "type", "insulationClass"].includes(name)) {
      if (!/^[a-zA-Z\/\ ]*$/.test(value)) error = "Only alphabetic characters are allowed";
    } else if (["customer", "client"].includes(name)) {
      if (!/^[a-zA-Z\s]*$/.test(value)) error = "Only alphabetic characters are allowed";
    } else if (name === "project") {
      if (!/^[a-zA-Z0-9\s, ]*$/.test(value)) error = "Only alphabetic and numeric characters are allowed";
    } else if (["reportNo", "location"].includes(name)) {
      if (!/^[a-zA-Z0-9\s-,]*$/.test(value)) error = "Only alphabetic characters, numbers, and '-' are allowed";
    } else if (name === "il") {
      if (!/^[a-zA-Z0-9\s/]*$/.test(value)) error = "Only alphabetic characters, numbers, and '/' are allowed";
    } else if (name === "vf") {
      if (!/^[a-zA-Z0-9\s.&]*$/.test(value)) error = "Only alphabetic characters, numbers, float values, and '&' are allowed";
    } else if (name === "specNo") {
      if (!/^[a-zA-Z0-9\s:-\s/&]*$/.test(value)) error = "Only alphabetic characters, numbers, and ':' are allowed";
    } else if (name === "make") {
      if (!/^[a-zA-Z\s0-9]*$/.test(value)) error = "Only alphabetic characters are allowed";
    } else if (name === "yom") {
      const currentYear = new Date().getFullYear();
      if (!/^\d{0,4}$/.test(value)) error = "Only 4 digit year is allowed";
      else if (parseInt(value, 10) > currentYear) error = `Year cannot be greater than ${currentYear}`;
    } else if (name === "serialNo") {
      if (!/^[0-9/s, ]*$/.test(value)) error = "Only numbers and '/' are allowed";
    }

    if (error) {
      setErrorMsg(error);
      setTimeout(() => setErrorMsg(""), 3000);
      newVal = allFormData.general[name];
    } else {
      newVal = value;
    }

    setAllFormData(prev => ({
      ...prev,
      general: { ...prev.general, [name]: newVal }
    }));
  };

  const handleDetailChange = (index, key, value) => {
    const updated = [...windingDetails];
    updated[index][key] = value;
    setWindingDetails(updated);
    setAllFormData(prev => ({
      ...prev,
      windingDetails: updated
    }));
  };

  const handleNext = () => setCurrentPage("insulation");

  const handleBack = () => {
    const pages = ["home", "insulation", "checking", "ratio", "checklist"];
    const idx = pages.indexOf(currentPage);
    setCurrentPage(pages[Math.max(0, idx - 1)]);
  };

  const updateFormData = (section, data) => {
    setAllFormData(prev => ({ ...prev, [section]: data }));
  };

  const renderHomePage = () => (
    <div className="vt-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="vt-head">VOLTAGE INSPECTION / UPDATE TEST REPORT</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              fontSize: '20px',
              fontWeight: '600',
              color: '#333',
              cursor: 'pointer',
              marginLeft: '10px'
            }}
            onClick={goBack}
          >
            X
          </span>
        </div>
      </div>

      {errorMsg && <div className="error-msg">{errorMsg}</div>}<br />

      <div className="grid">
        {["description", "date", "customer", "project", "reportNo", "location", "client"].map(field => (
          <label key={field}>
            {field.charAt(0).toUpperCase() + field.slice(1)}
            <input
              name={field}
              type={field === "date" ? "date" : "text"}
              value={allFormData.general[field] || ""}
              maxLength={40}
              onChange={handleFormChange}
              placeholder={`Enter ${field}`}
            />
          </label>
        ))}
      </div>

      <h3 className="vt-head1">NAME PLATE DETAILS  </h3><br />
      <div className="grid">
        {[
          { name: "yom", label: "YOM (Year of Manufacturing)" },
          { name: "il", label: "IL (Insulation Level)" },
          { name: "vf" },
          { name: "frequency" },
          { name: "type" },
          { name: "specNo" },
          { name: "insulationClass", label: "Insulation Class" },
          { name: "make" },
          { name: "serialNo", label: "Serial No" }
        ].map(({ name, label }) => (
          <label key={name}>
            {(label || name.toUpperCase())}
            {name === "frequency" ? (
              <select name={name} value={allFormData.general[name] || ""} onChange={handleFormChange}>
                <option value="">Select Frequency</option>
                <option value="50HZ">50HZ</option>
                <option value="60HZ">60HZ</option>
              </select>
            ) : (
              <input
                name={name}
                value={allFormData.general[name] || ""}
                maxLength={40}
                onChange={handleFormChange}
                placeholder={`Enter ${label || name}`}
              />
            )}
          </label>
        ))}
      </div>

      <label>No of Windings:
        <select value={selectedWinding} onChange={(e) => setSelectedWinding(e.target.value)}>
          <option value="">Select Winding</option>
          {windings.map(w => <option key={w} value={w}>{w}</option>)}
        </select>
      </label>

      {windingDetails.map((detail, idx) => (
        <fieldset key={idx} className="winding-section">
          <legend><h4>Winding {idx + 1} Details</h4></legend>

          <label>Ratio
            <input
              type="text"
              value={detail.ratio || ''}
              maxLength={20}
              onChange={(e) => handleDetailChange(idx, "ratio", e.target.value)}
              placeholder="Enter Ratio"
            />
          </label>

          <label>VA/PH
            <input
              type="text"
              value={detail.vaPh || ''}
              maxLength={20}
              onChange={(e) => handleDetailChange(idx, "vaPh", e.target.value)}
              placeholder="Enter VA/PH"
            />
          </label>

          <label>Class
            <select
              value={detail.class || ''}
              onChange={(e) => handleDetailChange(idx, "class", e.target.value)}>
              <option value="">Select Class</option>
              {kneePointTypes.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>

          <label>Volts
            <input
              type="text"
              value={detail.volts || ''}
              maxLength={20}
              onChange={(e) => handleDetailChange(idx, "volts", e.target.value)}
              placeholder="Enter Volts"
            />
          </label>
        </fieldset>
      ))}

      <div className="button-group">
        <button type="button" onClick={goBack}>Back</button>
        <button type="button" className="nxt1" onClick={handleNext}>Next</button>
      </div>
    </div>
  );

  const renderPage = () => {
    switch (currentPage) {
      case "home": return renderHomePage();
      case "insulation": return <VTInsulationUpdate goBack={handleBack} goToNext={() => setCurrentPage("checking")} formData={allFormData} updateFormData={(data) => updateFormData("insulationData", data)} />;
      case "checking": return <VTCheckingUpdate goBack={handleBack} goToNext={() => setCurrentPage("ratio")} formData={allFormData} updateResistance={(data) => updateFormData("windingResistance", data)} updatePolarity={(data) => updateFormData("polarityCheck", data)} />;
      case "ratio": return <VTRatioUpdate goBack={handleBack} goToNext={() => setCurrentPage("checklist")} formData={allFormData} updateRatioData={(data) => updateFormData("ratioTestData", data)} />;
      case "checklist": return <VTListUpdate goBack={handleBack} updateChecklist={(data) => updateFormData("checklistData", data)} formData={allFormData} />;
      default: return renderHomePage();
    }
  };

  return showBackPage ? <CreateReport goBack={() => setShowBackPage(false)} savedGeneralFormData={allFormData} /> : renderPage();
};

export default VTHomeUpdate;